import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

import type { AccessKey } from '@/api/admin-sales-channels-types'
import { preloadGetPointOfSale, useGetPointOfSale } from '@/api/react-query'
import { AccessKeyDevelopmentModeCell } from '@/components/access-key/create-access-key-drawer'
import { Badge } from '@/components/base/badge'
import { Container, ContainerHeader } from '@/components/base/container'
import { Copy } from '@/components/base/copy'
import { IconButton } from '@/components/base/icon-button'
import { SectionRow } from '@/components/base/section-row'
import { Text } from '@/components/base/text'
import { SingleColumnPage } from '@/components/page-layout'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { PencilSquare } from '@/icons/pencil-square'
import { Plus } from '@/icons/plus'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

export const Route = createFileRoute('/_authenticated/settings/point-of-sale/$pointOfSaleUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit']}>
			<PointOfSaleDetail />
		</ConditionalRouteComponent>
	),
	loader: async ({ context, params: { pointOfSaleUuid } }) => {
		await preloadGetPointOfSale(context, { path: { pointOfSaleUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.PointOfSale.Detail.Title()
	}
})

function PointOfSaleDetail() {
	const { L } = useI18n()
	const { pointOfSaleUuid } = Route.useParams()

	const pointOfSale = useGetPointOfSale({ pointOfSaleUuid })

	const accessKeyColumnDefs = useMemo<ColDef<AccessKey>[]>(
		() => [
			{
				headerName: L.Fields.Name(),
				field: 'name',
				flex: 2
			},
			{
				headerName: L.Settings.SalesChannels.AccessKeyType.Title(),
				field: 'type',
				flex: 1,
				valueGetter: ({ data }) =>
					data?.type === 'SECRET'
						? L.Settings.SalesChannels.AccessKeyType.Api()
						: L.Settings.SalesChannels.AccessKeyType.Publishable()
			},
			{
				headerName: L.Settings.PointOfSale.AccessKeys.Column.Key(),
				flex: 2,
				cellRenderer: ({ data }: { data: AccessKey }) => {
					if (!data) return null
					const isSecret = data.type === 'SECRET'
					const display = isSecret ? data.redacted : `${data.token.substring(0, 8)}...`
					return (
						<div className='flex h-full items-center gap-2'>
							<code className='rounded bg-ui-bg-subtle px-2 py-1 text-xs'>{display}</code>
							{!isSecret && <Copy content={data.token} variant='mini' />}
						</div>
					)
				}
			},
			{
				headerName: L.Fields.CreatedAt(),
				field: 'createdAt',
				type: 'date',
				flex: 1
			},
			{
				headerName: L.Settings.AccessKeys.Field.DevelopmentMode(),
				flex: 1,
				cellRenderer: AccessKeyDevelopmentModeCell
			}
		],
		[L]
	)

	return (
		<SingleColumnPage hasOutlet>
			{/* General */}
			<Container className='divide-y'>
				<ContainerHeader
					heading={pointOfSale.data?.name || L.Settings.PointOfSale.Detail.Title()}
					rightSide={
						<Link from={Route.fullPath} to='edit'>
							<IconButton tooltipText={L.General.Edit()}>
								<PencilSquare />
							</IconButton>
						</Link>
					}
				/>
				<SectionRow title={L.Fields.Name()} value={pointOfSale.data?.name ?? '-'} />
				<SectionRow
					title={L.Settings.PointOfSale.Column.Identifier()}
					value={pointOfSale.data?.identifier || L.General.NotSet()}
				/>
			</Container>

			{/* Sales Channels */}
			<Container className='divide-y'>
				<ContainerHeader heading={L.Fields.SalesChannel()} />
				{pointOfSale.data?.salesChannels && pointOfSale.data.salesChannels.length > 0 ? (
					pointOfSale.data.salesChannels.map(channel => (
						<Link
							key={channel.uuid}
							to='/settings/sales-channels/$salesChannelUuid'
							params={{ salesChannelUuid: channel.uuid }}
						>
							<SectionRow title={channel.name} value='' />
						</Link>
					))
				) : (
					<div className='px-6 py-4'>
						<Text size='small' leading='compact' className='text-ui-fg-muted'>
							{L.General.NoResultsTitle()}
						</Text>
					</div>
				)}
			</Container>

			{/* Access Keys */}
			<Container className='flex flex-1 flex-col'>
				<ContainerHeader
					heading={L.Settings.PointOfSale.Detail.AccessKeys()}
					rightSide={
						<IconButton tooltipText={L.General.Create()} disabled>
							<Plus />
						</IconButton>
					}
				/>
				<Table
					className='w-full flex-1'
					data={pointOfSale.data?.accessKeys}
					columnUtilities={{}}
					columnDefs={accessKeyColumnDefs}
					animateRows
					suppressCellFocus
				/>
			</Container>
		</SingleColumnPage>
	)
}
